import { httpGetAsync } from '../utils/HttpClient';
import { authService } from '..';

export interface FinanceQuoteFieldDiff {
  fieldName: string;
  displayName: string;
  printedValue: string;
  currentValue: string;
}

export interface DeferredPaymentSnapshot {
  paymentDate: string;
  amount: number;
}

export interface DeferredPaymentCollectionDiff {
  hasDifferences: boolean;
  printedCount: number;
  currentCount: number;
  printedPayments: DeferredPaymentSnapshot[];
  currentPayments: DeferredPaymentSnapshot[];
}

export interface IrregularPaymentStreamSnapshot {
  startDate: string;
  endDate: string;
  amount: number;
  handling: string;
  type: string;
  count: number;
  paymentFrequency: string;
}

export interface IrregularPaymentStreamCollectionDiff {
  hasDifferences: boolean;
  printedCount: number;
  currentCount: number;
  printedStreams: IrregularPaymentStreamSnapshot[];
  currentStreams: IrregularPaymentStreamSnapshot[];
}

export interface FinanceQuoteComparisonResult {
  hasPrintHistory: boolean;
  hasDifferences: boolean;
  printedAt: string | null;
  printTimeHistoryId: string | null;
  currentHistoryId: string | null;
  differences: FinanceQuoteFieldDiff[];
  deferredPaymentDiffs: DeferredPaymentCollectionDiff | null;
  irregularPaymentStreamDiffs: IrregularPaymentStreamCollectionDiff | null;
}

/**
 * Fetches the comparison between the finance quote at the time of document printing
 * and the current finance quote state. Used by the loan posting validation chain
 * to detect if the quote changed after documents were printed.
 */
export const getFinanceQuoteComparison = async (
  applicationId: string,
  financeQuoteId: string
): Promise<FinanceQuoteComparisonResult | null> => {
  try {
    const bearerToken = await authService.getToken();
    if (!bearerToken) return null;

    const response = await httpGetAsync(
      `api/applicationFinanceQuote/CompareQuoteAtPrintTime?applicationId=${applicationId}&financeQuoteId=${financeQuoteId}`,
      bearerToken
    );
    if (!response || !response.ok) return null;

    return await response.json() as FinanceQuoteComparisonResult;
  } catch (err) {
    console.error('Error fetching finance quote comparison:', err);
    return null;
  }
};

export interface ApplicationPrintStatus {
  canPrint: boolean;
  hasPrinted: boolean;
}

/**
 * Checks whether the application has documents available for printing and whether
 * documents have already been printed. Used by the loan posting validation chain
 * to warn users who haven't printed docs before posting.
 */
export const getApplicationPrintStatus = async (
  applicationId: string
): Promise<ApplicationPrintStatus | null> => {
  try {
    const bearerToken = await authService.getToken();
    if (!bearerToken) return null;

    const response = await httpGetAsync(
      `api/documentPrint/hasPrinted/${applicationId}`,
      bearerToken
    );
    if (!response || !response.ok) return null;

    return await response.json() as ApplicationPrintStatus;
  } catch (err) {
    console.error('Error checking application print status:', err);
    return null;
  }
};
